// MobileScale — 화면 전체를 확대·축소(가독성). 효과만 있고 렌더 없음(null).
// 이유: 앱 전반이 인라인 px 폰트(수백 곳)라 CSS로 개별 확대 불가 → 루트에 zoom을 걸어 일괄 확대.
//       zoom은 자식 <iframe> 렌더링도 함께 확대하므로 임베드된 MM/NC 메뉴 글씨도 같이 커진다(한 레버로 둘 다).
// 분기 = useIsMobile(폭 OR 터치 OR 수동 override).
//   · 모바일: 배율 = utils/mobileZoom(gwMobileZoom · 모바일 서랍 SidebarViewControls 의 −/+) + 핀치 차단·키보드 보정.
//   · 웹: 배율 = utils/desktopZoom(gwDesktopZoom · 사이드바 하단 SidebarViewControls 의 −/+). 기본 1.0 이면
//     <html> 인라인 값을 전혀 건드리지 않는다(기존 100% 그대로). 1.0 이 아니면 zoom 과 --gw-app-h 만 건다.
//   두 분기는 같은 복원 함수(readInlineProperty·restoreInlineProperty)를 쓴다 — 모바일↔웹 전환 때 React 가
//   바뀐 effect 의 cleanup 을 먼저 모두 돌리므로 서로의 인라인 값이 남지 않는다. 변경 시 이벤트로 즉시 재적용.
//
// ⚠️ zoom↔뷰포트 보정(--gw-app-h): zoom을 <html>에 걸면 100dvh 컨테이너가 zoom배만큼 커져(예: 1.5× → 150dvh)
//    가시 뷰포트를 넘쳐 모든 화면에 불필요한 상하 스크롤·하단 공백이 생긴다. 그래서 '가시 뷰포트 높이 ÷ zoom'을
//    CSS 변수 --gw-app-h(px)로 노출하고, 페이지 셸이 height:var(--gw-app-h, 100dvh)로 이 값을 쓰면
//    zoom 적용 후 정확히 한 화면(=가시영역)을 채운다. 웹 1.0 은 변수 없음 → 100dvh 폴백(=100vh, 무영향).
import { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import useVisualViewport from '../hooks/useVisualViewport';
import {
  getMobileZoom,
  getViewportSafeMobileZoom,
  MIN_MOBILE_LAYOUT_WIDTH,
  MOBILE_ZOOM_EVENT,
} from '../utils/mobileZoom';
import { getDesktopZoom, DESKTOP_ZOOM_EVENT } from '../utils/desktopZoom';

const STYLE_PROPERTY_NAMES = { zoom: 'zoom', 'touch-action': 'touchAction' };

const readViewportWidth = () => (
  typeof window !== 'undefined' && Number.isFinite(window.innerWidth)
    ? window.innerWidth
    : MIN_MOBILE_LAYOUT_WIDTH
);

const readInlineProperty = (style, name) => {
  const value = style.getPropertyValue(name);
  return {
    value: value || style[STYLE_PROPERTY_NAMES[name]] || '',
    priority: value ? style.getPropertyPriority(name) : '',
  };
};

const setInlineProperty = (style, name, value, priority = '') => {
  style.setProperty(name, value, priority);
  const propertyName = STYLE_PROPERTY_NAMES[name];
  if (propertyName && !style.getPropertyValue(name)) style[propertyName] = value;
};

const restoreInlineProperty = (style, name, previous) => {
  if (previous.value) setInlineProperty(style, name, previous.value, previous.priority);
  else {
    style.removeProperty(name);
    const propertyName = STYLE_PROPERTY_NAMES[name];
    if (propertyName) style[propertyName] = '';
  }
};

export default function MobileScale() {
  const isMobile = useIsMobile();
  const { viewportHeight, keyboardOpen, keyboardHeight, offsetTop } = useVisualViewport();
  const [zoom, setZoom] = useState(getMobileZoom);
  const [desktopZoom, setDesktopZoomState] = useState(getDesktopZoom);
  const [viewportWidth, setViewportWidth] = useState(readViewportWidth);
  useEffect(() => {
    const sync = () => setZoom(getMobileZoom());
    window.addEventListener(MOBILE_ZOOM_EVENT, sync);   // 같은 탭 +/- 조절
    window.addEventListener('storage', sync);           // 다른 탭 동기화
    return () => {
      window.removeEventListener(MOBILE_ZOOM_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  useEffect(() => {
    const sync = () => setDesktopZoomState(getDesktopZoom());
    window.addEventListener(DESKTOP_ZOOM_EVENT, sync);  // 같은 탭 +/- 조절
    window.addEventListener('storage', sync);           // 다른 탭 동기화
    return () => {
      window.removeEventListener(DESKTOP_ZOOM_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  useEffect(() => {
    if (!isMobile) return undefined;
    const sync = () => setViewportWidth(readViewportWidth());
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [isMobile]);
  useEffect(() => {
    if (!isMobile) return undefined;

    const documents = new Map();
    const frames = new Map();
    const eventOptions = { passive: false };

    const forEachFrame = (node, callback) => {
      if (!node || node.nodeType !== 1) return;
      if (node.tagName === 'IFRAME') callback(node);
      node.querySelectorAll?.('iframe').forEach(callback);
    };

    function detachDocument(doc) {
      const record = documents.get(doc);
      if (!record) return;
      documents.delete(doc);
      record.observer?.disconnect();
      Array.from(frames.keys())
        .filter((frame) => frame.ownerDocument === doc)
        .forEach(detachFrame);
      doc.removeEventListener('touchmove', record.onTouchMove, eventOptions);
      doc.removeEventListener('gesturestart', record.onGesture, eventOptions);
      doc.removeEventListener('gesturechange', record.onGesture, eventOptions);
      if (record.root && record.touchAction) {
        restoreInlineProperty(record.root.style, 'touch-action', record.touchAction);
      }
    }

    function detachFrame(frame) {
      const record = frames.get(frame);
      if (!record) return;
      frames.delete(frame);
      frame.removeEventListener('load', record.onLoad);
      if (record.document) detachDocument(record.document);
    }

    function attachFrameDocument(frame) {
      const record = frames.get(frame);
      if (!record) return;
      let nextDocument = null;
      try {
        nextDocument = frame.contentDocument;
      } catch (e) {
        // 교차 출처 iframe은 동일 출처 정책상 부모 문서에서 제어할 수 없다.
      }
      if (record.document === nextDocument) return;
      if (record.document) detachDocument(record.document);
      record.document = nextDocument;
      if (nextDocument) attachDocument(nextDocument);
    }

    function attachFrame(frame) {
      if (!frames.has(frame)) {
        const onLoad = () => attachFrameDocument(frame);
        frames.set(frame, { onLoad, document: null });
        frame.addEventListener('load', onLoad);
      }
      attachFrameDocument(frame);
    }

    function attachDocument(doc) {
      if (!doc || documents.has(doc)) return;

      const root = doc.documentElement;
      const touchAction = root ? readInlineProperty(root.style, 'touch-action') : null;
      const onTouchMove = (event) => {
        if (event.touches && event.touches.length >= 2) event.preventDefault();
      };
      const onGesture = (event) => event.preventDefault();

      doc.addEventListener('touchmove', onTouchMove, eventOptions);
      doc.addEventListener('gesturestart', onGesture, eventOptions);
      doc.addEventListener('gesturechange', onGesture, eventOptions);
      if (root) setInlineProperty(root.style, 'touch-action', 'pan-x pan-y');

      const record = { root, touchAction, onTouchMove, onGesture, observer: null };
      documents.set(doc, record);
      if (typeof MutationObserver !== 'undefined') {
        record.observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((node) => forEachFrame(node, detachFrame));
            mutation.addedNodes.forEach((node) => forEachFrame(node, attachFrame));
          });
        });
        record.observer.observe(root || doc, { childList: true, subtree: true });
      }
      doc.querySelectorAll?.('iframe').forEach(attachFrame);
    }

    attachDocument(document);

    return () => {
      Array.from(frames.keys()).forEach(detachFrame);
      Array.from(documents.keys()).forEach(detachDocument);
    };
  }, [isMobile]);
  useEffect(() => {
    const el = document.documentElement;   // <html>
    if (!isMobile) return undefined;       // 웹: 이 effect 는 소유하지 않음(아래 웹 배율 effect)
    const appliedZoom = getViewportSafeMobileZoom(zoom, viewportWidth);

    const previous = {
      zoom: readInlineProperty(el.style, 'zoom'),
      appHeight: readInlineProperty(el.style, '--gw-app-h'),
      visibleHeight: readInlineProperty(el.style, '--gw-vv-h'),
      keyboardHeight: readInlineProperty(el.style, '--gw-kb-h'),
      viewportTop: readInlineProperty(el.style, '--gw-vv-top'),
      hasKeyboard: Object.prototype.hasOwnProperty.call(el.dataset, 'gwKeyboard'),
      keyboard: el.dataset.gwKeyboard,
    };

    setInlineProperty(el.style, 'zoom', String(appliedZoom));
    // 가시 뷰포트(URL바 제외) ÷ zoom → 페이지 셸이 zoom 후 정확히 한 화면을 채우게 함.
    // 회귀 검증: 채팅 e2e는 visualViewport resize 후 이 값의 축소와 composer 가시성을 함께 확인한다.
    const h = Math.max(320, viewportHeight / appliedZoom);
    el.style.setProperty('--gw-app-h', h + 'px');
    // --gw-vv-h = 하한 없는 가시 높이(g2778). --gw-app-h 는 320 하한 때문에 가시 화면보다 길 수 있다(844×390 · 150% → 320 > 260).
    //   position:fixed 층(MobileDrawer 패널·서랍 안 aside·«내 계정» 창)은 이 값을 써야 바닥이 화면 안에 든다. 페이지 셸은 계속 --gw-app-h(하한 유지).
    el.style.setProperty('--gw-vv-h', (viewportHeight / appliedZoom) + 'px');
    el.style.setProperty('--gw-kb-h', (keyboardHeight / appliedZoom) + 'px');
    el.style.setProperty('--gw-vv-top', (offsetTop / appliedZoom) + 'px');
    if (keyboardOpen) el.dataset.gwKeyboard = '1';
    else delete el.dataset.gwKeyboard;

    let scrollFrame = null;
    const scrollingElement = document.scrollingElement;
    if (keyboardOpen && scrollingElement && scrollingElement.scrollTop !== 0) {
      scrollFrame = window.requestAnimationFrame(() => window.scrollTo(0, 0));
    }
    return () => {
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
      restoreInlineProperty(el.style, 'zoom', previous.zoom);
      restoreInlineProperty(el.style, '--gw-app-h', previous.appHeight);
      restoreInlineProperty(el.style, '--gw-vv-h', previous.visibleHeight);
      restoreInlineProperty(el.style, '--gw-kb-h', previous.keyboardHeight);
      restoreInlineProperty(el.style, '--gw-vv-top', previous.viewportTop);
      if (previous.hasKeyboard) el.dataset.gwKeyboard = previous.keyboard;
      else delete el.dataset.gwKeyboard;
    };
  }, [isMobile, zoom, viewportWidth, viewportHeight, keyboardOpen, keyboardHeight, offsetTop]);
  // 웹 배율 — 1.0 이면 아무것도 걸지 않는다. 그 밖이면 zoom 과 --gw-app-h(= innerHeight ÷ zoom)만 걸고,
  //   창 크기가 바뀌면 다시 잰다. cleanup 은 잡아 둔 원래 인라인 값으로 되돌린다(모바일 effect 와 같은 규칙).
  useEffect(() => {
    if (isMobile) return undefined;                       // 모바일은 위 effect 가 소유
    if (Math.abs(desktopZoom - 1) < 0.001) return undefined;
    const el = document.documentElement;   // <html>
    const previous = {
      zoom: readInlineProperty(el.style, 'zoom'),
      appHeight: readInlineProperty(el.style, '--gw-app-h'),
    };
    const apply = () => {
      setInlineProperty(el.style, 'zoom', String(desktopZoom));
      el.style.setProperty('--gw-app-h', (window.innerHeight / desktopZoom) + 'px');
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      restoreInlineProperty(el.style, 'zoom', previous.zoom);
      restoreInlineProperty(el.style, '--gw-app-h', previous.appHeight);
    };
  }, [isMobile, desktopZoom]);
  return null;
}
